import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { FaArrowLeft, FaCreditCard } from "react-icons/fa";
import validator from "validator";
import { db } from "../config/Firebase";
import { doc, getDoc, setDoc, enableIndexedDbPersistence } from "firebase/firestore";

// Enable Firestore offline persistence
enableIndexedDbPersistence(db).catch((err) => {
  console.warn("Firestore persistence failed:", err);
});

// Configuration for Email Service Endpoint
const EMAIL_SERVICE_ENDPOINT = "https://aramemail.outliersunited.com/send-order-email";

// Load Razorpay SDK dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function OrderDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { product, lensOption, leftSpherical, leftCylindrical, leftAxis, rightSpherical, rightCylindrical, rightAxis, totalCost } = location?.state || {};

  // State management
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    pincode: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [firestoreError, setFirestoreError] = useState(null);

  // Handles input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Validates form fields
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Full Name is required";
    if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Valid 10-digit phone number is required";
    }
    if (!validator.isEmail(formData.email)) {
      newErrors.email = "Valid email address is required";
    }
    if (!formData.address.trim()) newErrors.address = "Delivery Address is required";
    if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Valid 6-digit pincode is required";
    }
    return newErrors;
  };

  // Store order in Firestore with retry logic
  const storeOrderInFirestore = async (paymentId, orderId, maxRetries = 3) => {
    let attempts = 0;
    let lastError = null;

    while (attempts < maxRetries) {
      attempts++;
      try {
        const lensType = lensOption?.name || "Only Frame";
        const lensOptionName = lensOption?.id === 1 && lensOption?.details?.name
          ? lensOption.details.name
          : null;

        const orderEntry = {
          customerDetails: {
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            address: formData.address,
            pincode: formData.pincode,
          },
          productDetails: {
            productID: product?.ProductID || "N/A",
            name: product?.Name || "Black Jack",
            cost: product?.Cost || 1000,
            frameColor: product?.FrameColor || "N/A",
            material: product?.Material || "N/A",
            shape: product?.Shape || "N/A",
          },
          lensOption: {
            name: lensType,
            lensOptions: lensOptionName,
            id: lensOption?.id || 4,
            leftSpherical: leftSpherical || 0,
            leftCylindrical: leftCylindrical || 0,
            leftAxis: leftAxis || 0,
            rightSpherical: rightSpherical || 0,
            rightCylindrical: rightCylindrical || 0,
            rightAxis: rightAxis || 0,
          },
          totalAmount: totalCost || 1100,
          paymentId: paymentId || "N/A",
          razorpayOrderId: orderId || "N/A",
          timestamp: new Date().toISOString(),
          status: "order confirmed",
        };

        const docRef = doc(db, "detailsorder", formData.email);
        const docSnap = await getDoc(docRef);

        let orderData;
        if (docSnap.exists()) {
          const existingData = docSnap.data();
          orderData = {
            orders: [...existingData.orders, orderEntry],
          };
        } else {
          orderData = {
            orders: [orderEntry],
          };
        }

        await setDoc(docRef, orderData);
        return { success: true, orderId: formData.email };
      } catch (error) {
        lastError = error;
        console.error(`Firestore attempt ${attempts}/${maxRetries} failed:`, error);
        if (attempts < maxRetries && error.message.includes("offline")) {
          console.log(`Retrying Firestore operation... (${attempts}/${maxRetries})`);
          await new Promise((resolve) => setTimeout(resolve, 2000));
        } else {
          break;
        }
      }
    }

    setFirestoreError(`Failed to save order after ${maxRetries} attempts: ${lastError.message}. Contact support at support@arameyecare.com.`);
    return { success: false, error: lastError.message };
  };

  // Send order confirmation email with enhanced error handling
  const sendOrderEmail = async () => {
    const maxAttempts = 3;
    let attempts = 0;
    let lastError = null;

    while (attempts < maxAttempts) {
      attempts++;
      try {
        const baseCost = Number(String(product?.Cost || 1000).replace(/[^0-9.]/g, ""));
        const lensPrice = lensOption?.details?.basePrice || lensOption?.basePrice || 0;
        const totalCostFormatted = (totalCost || baseCost + lensPrice).toFixed(2);

        const lensType = lensOption?.name || "Only Frame";
        const lensOptionName = lensOption?.id === 1 && lensOption?.details?.name
          ? lensOption.details.name
          : null;
        const lensDescription = lensOptionName ? `${lensType} (${lensOptionName})` : lensType;

        const payload = {
          productId: product?.ProductID || "N/A",
          finalCost: totalCostFormatted,
          customerName: formData.name.trim(),
          customerEmail: formData.email.trim(),
          phoneNumber: formData.phone.trim(),
          lensType: lensDescription,
        };

        console.log(`Sending email attempt ${attempts}/${maxAttempts} to ${EMAIL_SERVICE_ENDPOINT} with payload:`, JSON.stringify(payload));

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        const response = await fetch(EMAIL_SERVICE_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Origin": window.location.origin,
          },
          body: JSON.stringify(payload),
          mode: "cors",
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Check if response is JSON
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const text = await response.text();
          console.error(`Non-JSON response received:`, { status: response.status, text });
          throw new Error(`Server returned non-JSON response (status ${response.status}): ${text.slice(0, 100)}...`);
        }

        const result = await response.json();

        if (!response.ok) {
          console.error(`Server responded with status ${response.status}:`, result);
          throw new Error(
            result.error || `HTTP error: ${response.status} ${response.statusText}`
          );
        }

        if (result.success) {
          console.log(`Order confirmation email sent successfully via ${EMAIL_SERVICE_ENDPOINT}`);
          return { success: true };
        } else {
          throw new Error(result.error || "Unknown server error during email sending.");
        }
      } catch (error) {
        lastError = error;
        console.error(`Email attempt ${attempts}/${maxAttempts} failed at ${EMAIL_SERVICE_ENDPOINT}:`, {
          message: error.message,
          name: error.name,
          stack: error.stack,
        });

        if (attempts < maxAttempts && (error.name === "AbortError" || error.message.includes("Failed to fetch"))) {
          console.log(`Retrying due to network error/timeout... (${attempts}/${maxAttempts})`);
          await new Promise((resolve) => setTimeout(resolve, 3000));
        } else {
          break;
        }
      }
    }

    const userErrorMessage = lastError.name === "AbortError"
      ? "Email server took too long to respond. Please check your inbox or spam folder."
      : lastError.message.includes("Failed to fetch")
        ? "Unable to connect to email server. Please check your internet connection."
        : lastError.message.includes("non-JSON response")
          ? "Email server returned an unexpected response. Please check your inbox or spam folder."
          : `Email sending failed: ${lastError.message}`;

    setEmailError(`${userErrorMessage} If not received, contact support at support@arameyecare.com with your payment ID.`);
    return { success: false, error: lastError.message };
  };

  // Initiate Razorpay Payment
  const initiatePayment = async () => {
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      alert("Failed to load payment gateway. Please check your internet connection and try again.");
      return { success: false };
    }

    try {
      const lensType = lensOption?.name || "Only Frame";
      const lensOptionName = lensOption?.id === 1 && lensOption?.details?.name
        ? lensOption.details.name
        : null;
      const lensDescription = lensOptionName ? `${lensType} (${lensOptionName})` : lensType;

      const response = await fetch("https://arameyecare.com/server.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: (totalCost || 1100) * 100,
          currency: "INR",
          receipt: `receipt_${Date.now()}`,
          product_id: product?.ProductID || "N/A",
          lens_option: lensDescription,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create payment order on server.");
      }

      const order = await response.json();

      const options = {
        key: "rzp_live_l3g3IfHjs4N8Ux",
        amount: order.amount,
        currency: order.currency,
        name: "Aram Eyecare",
        description: `Order for ${product?.Name || "Black Jack"}`,
        image: "https://arameyecare.com/logo.png",
        order_id: order.id,
        handler: async function (response) {
          if (!response.razorpay_payment_id || !response.razorpay_order_id) {
            alert("Invalid payment response. Please contact support at support@arameyecare.com.");
            return;
          }
          setIsPaymentLoading(true);
          setLoadingStage("paymentSuccess");

          try {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            setLoadingStage("emailProcessing");

            const firestoreResult = await storeOrderInFirestore(
              response.razorpay_payment_id,
              response.razorpay_order_id
            );
            if (!firestoreResult.success) {
              throw new Error(firestoreResult.error || "Failed to save order details to database.");
            }

            const emailResult = await sendOrderEmail();
            if (!emailResult.success) {
              console.warn("Order saved, but email sending reported failure:", emailResult.error);
              alert(
                `Order placed successfully, but email sending reported an issue: ${emailResult.error}. Please check your inbox and spam folder. If not received, contact support at support@arameyecare.com with payment ID: ${response.razorpay_payment_id}.`
              );
            } else {
              alert("Order placed and confirmation email sent successfully!");
            }

            navigate("/OrderConfirmation", {
              state: {
                orderDetails: {
                  ...formData,
                  totalAmount: totalCost,
                  paymentId: response.razorpay_payment_id,
                  orderId: response.razorpay_order_id,
                },
                product,
                lensOption,
              },
            });
          } catch (error) {
            console.error("Post-payment processing error:", error);
            alert(
              `Order may have been placed, but an error occurred: ${error.message}. Please verify your order status and contact support at support@arameyecare.com with payment ID: ${response.razorpay_payment_id}.`
            );
          } finally {
            setIsPaymentLoading(false);
            setLoadingStage(null);
            setEmailError(null);
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone,
        },
        notes: {
          address: formData.address,
          product_id: product?.ProductID || "N/A",
          lens_option: lensDescription,
        },
        theme: {
          color: "#000000",
        },
        modal: {
          ondismiss: function () {
            alert("Payment window closed. Please try again to complete your order.");
          },
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.on("payment.failed", function (response) {
        console.error("Razorpay payment failed:", response.error);
        alert(`Payment failed: ${response.error.description}. Please try again or contact support at support@arameyecare.com.`);
      });
      paymentObject.open();

      return { success: true };
    } catch (error) {
      console.error("Payment initiation error:", error);
      alert(`Unable to initiate payment: ${error.message}. Please try again or contact support at support@arameyecare.com.`);
      return { success: false };
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsLoading(true);
    setEmailError(null);
    setFirestoreError(null);

    const paymentResult = await initiatePayment();
    if (!paymentResult.success) {
      setIsPaymentLoading(false);
      setLoadingStage(null);
    }
    setIsLoading(false);
  };

  // Lens display logic
  const lensType = lensOption?.name || "Only Frame";
  const lensOptionName = lensOption?.id === 1 && lensOption?.details?.name
    ? lensOption.details.name
    : null;

  return (
    <>
      <Header />
      <button
        onClick={() => navigate(-1)}
        className="fixed top-24 left-4 z-30 flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-all border border-gray-200"
      >
        <FaArrowLeft className="text-gray-700" />
        <span className="hidden sm:inline">Back</span>
      </button>
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">Order Details</h2>
            <p className="mt-2 text-sm text-gray-600">Please provide your details to complete the order</p>
          </div>
          <div className="space-y-8">
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h3 className="text-3xl font-bold text-black mb-4">Order Summary</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-bold text-black mb-2">Product Details</h4>
                  <div className="flex items-start gap-4">
                    <img
                      src={product?.ImageUrl1 || "https://placehold.co/96x96/E0E0E0/333333?text=Product"}
                      alt={product?.Name || "Product"}
                      className="w-24 h-24 object-cover rounded-lg shadow-sm"
                    />
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-700 flex-grow">
                      <span className="font-medium text-black">Product ID:</span>
                      <span className="font-medium text-black">{product?.ProductID || "N/A"}</span>
                      <span className="font-medium text-black">Name:</span>
                      <span className="font-medium text-black">{product?.Name || "Black Jack"}</span>
                      <span className="font-medium text-black">Frame Color:</span>
                      <span className="font-medium text-black">{product?.FrameColor || "N/A"}</span>
                      <span className="font-medium text-black">Material:</span>
                      <span className="font-medium text-black">{product?.Material || "N/A"}</span>
                      <span className="font-medium text-black">Shape:</span>
                      <span className="font-medium text-black">{product?.Shape || "N/A"}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-2">Lens Details</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                    <span className="font-medium">Lens Type:</span>
                    <span>{lensType}</span>
                    {lensOptionName && (
                      <>
                        <span className="font-medium">Lens Option:</span>
                        <span>{lensOptionName}</span>
                      </>
                    )}
                    {lensOption?.id === 1 && (
                      <>
                        <span className="font-medium">Left Spherical:</span>
                        <span>{leftSpherical || 0} D</span>
                        <span className="font-medium">Left Cylindrical:</span>
                        <span>{leftCylindrical || 0} D</span>
                        <span className="font-medium">Left Axis:</span>
                        <span>{leftAxis || 0}°</span>
                        <span className="font-medium">Right Spherical:</span>
                        <span>{rightSpherical || 0} D</span>
                        <span className="font-medium">Right Cylindrical:</span>
                        <span>{rightCylindrical || 0} D</span>
                        <span className="font-medium">Right Axis:</span>
                        <span>{rightAxis || 0}°</span>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-2">Cost Breakdown</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                    <span className="font-medium">Frame Cost:</span>
                    <span>₹{product?.Cost || 1000}</span>
                    <span className="font-medium">Lens Cost:</span>
                    <span>₹{lensOption?.details?.basePrice || lensOption?.basePrice || 0}</span>
                    <span className="font-bold text-gray-900 mt-2 text-lg">Total:</span>
                    <span className="font-bold text-gray-900 mt-2 text-lg">₹{totalCost || 1100}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {firestoreError && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-lg shadow-sm" role="alert">
              <p className="font-bold">Order Saving Issue</p>
              <p>{firestoreError}</p>
            </div>
          )}
          {emailError && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-lg shadow-sm" role="alert">
              <p className="font-bold">Email Sending Issue</p>
              <p>{emailError}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4 py-3 ${errors.name ? "border-red-500" : ""
                  }`}
                placeholder="John Doe"
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4 py-3 ${errors.phone ? "border-red-500" : ""
                  }`}
                placeholder="1234567890"
              />
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4 py-3 ${errors.email ? "border-red-500" : ""
                  }`}
                placeholder="example@domain.com"
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                Delivery Address
              </label>
              <textarea
                name="address"
                id="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="4"
                className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4 py-3 min-h-[120px] ${errors.address ? "border-red-500" : ""
                  }`}
                placeholder="123 Main St, Apt 4B, City"
              />
              {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address}</p>}
            </div>
            <div>
              <label htmlFor="pincode" className="block text-sm font-medium text-gray-700">
                Pincode
              </label>
              <input
                type="text"
                name="pincode"
                id="pincode"
                value={formData.pincode}
                onChange={handleInputChange}
                className={`mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4 py-3 ${errors.pincode ? "border-red-500" : ""
                  }`}
                placeholder="123456"
              />
              {errors.pincode && <p className="mt-1 text-sm text-red-600">{errors.pincode}</p>}
            </div>
            <button
              type="submit"
              disabled={isLoading || isPaymentLoading}
              className={`w-full flex items-center justify-center gap-2 py-4 px-4 bg-black text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-95 ${(isLoading || isPaymentLoading) ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              <FaCreditCard />
              {isLoading ? "Processing..." : `Pay ₹${totalCost || 1100}`}
            </button>
          </form>
        </div>
      </div>
      {isPaymentLoading && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
          role="alert"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full flex flex-col items-center transform animate-fadeIn">
            {loadingStage === "paymentSuccess" && (
              <>
                <div className="relative w-16 h-16 mb-4">
                  <svg
                    className="w-full h-full text-green-500 animate-checkmark"
                    width="52"
                    height="52"
                    viewBox="0 0 52 52"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="26" cy="26" r="25" fill="none" className="animate-circle" />
                    <path d="M14 27l9 9 15-18" className="animate-check" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h3>
                <p className="text-sm text-gray-600 text-center">
                  Your payment of ₹{totalCost || 1100} has been processed.
                </p>
              </>
            )}
            {loadingStage === "emailProcessing" && (
              <>
                <div className="relative w-16 h-16 mb-4">
                  <div className="animate-spin rounded-full h-full w-full border-t-4 border-b-4 border-blue-500"></div>
                  <svg
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-blue-500"
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 2l-8 5-8-5V6l8 5 8-5z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Processing Email...</h3>
                <p className="text-sm text-gray-600 text-center">
                  We're sending your order confirmation email. Please wait a moment.
                </p>
              </>
            )}
          </div>
        </div>
      )}
      <Footer />
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes circle {
          0% { stroke-dasharray: 0 150; stroke-dashoffset: 0; }
          50% { stroke-dasharray: 150 150; stroke-dashoffset: 0; }
          100% { stroke-dasharray: 150 150; stroke-dashoffset: 0; }
        }
        @keyframes check {
          0% { stroke-dasharray: 0 100; stroke-dashoffset: 0; }
          100% { stroke-dasharray: 100 100; stroke-dashoffset: 0; }
        }
        .animate-circle {
          stroke-dasharray: 150;
          animation: circle 1s ease-in-out;
        }
        .animate-check {
          stroke-dasharray: 100;
          animation: check 0.5s ease-in-out 0.5s forwards;
        }
        .animate-checkmark {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
}